import type { Deliverable } from "@/components/servicios/ServiceDeliverables";
import type { Phase } from "@/components/servicios/ServicePhases";
import type { ServiceFaq } from "@/components/servicios/ServiceFaqBlock";
import type { CrossLink } from "@/components/servicios/ServiceCrossLinks";

export type ServiceContent = {
  /** Nombre comercial del servicio (usado en schema Service) */
  name: string;
  /** Ruta canónica del servicio */
  path: string;
  /** Descripción corta para schema */
  description: string;
  deliverables: { title: string; lead: string; items: Deliverable[] };
  phases: { title: string; lead: string; items: Phase[] };
  fit: { title: string; lead: string; yes: string[]; no: string[] };
  faqs: { title: string; lead: string; items: ServiceFaq[] };
  crossLinks: { title: string; lead: string; items: CrossLink[] };
};

const CTA_LEAD =
  "Todo lo que sigue es trabajo ejecutado por el equipo senior. No prometemos posiciones: ejecutamos el trabajo profesional que las consigue.";

export const serviciosContenido: Record<string, ServiceContent> = {
  /* ------------------------------------------------------------------ */
  "seo-local-ia": {
    name: "SEO Local con IA",
    path: "/servicios/seo-local-ia",
    description:
      "Posicionamiento en Google Maps y búsquedas locales con exclusividad por sector y zona. Propuesta personalizada según mercado, competencia, alcance y objetivos; compromiso mínimo 3 meses.",
    deliverables: {
      title: "Qué incluye exactamente el SEO Local",
      lead: CTA_LEAD,
      items: [
        {
          title: "Auditoría y reconstrucción de la ficha de empresa",
          frequency: "Inicio",
          description:
            "Revisamos categorías primarias y secundarias, atributos, servicios, zonas de servicio, horarios y fotografías. Contrastamos la categoría y el resto de datos con los servicios reales del negocio.",
        },
        {
          title: "Mapa de búsquedas locales por barrio",
          frequency: "Inicio + trimestral",
          description:
            "Cruzamos volumen de búsqueda, intención transaccional y competencia real en cada distrito de tu zona. De ahí sale la lista de consultas por las que compites y el orden en el que las atacamos.",
        },
        {
          title: "Páginas de servicio y de zona en tu web",
          frequency: "Mensual",
          description:
            "Redactamos y publicamos páginas específicas para cada combinación servicio + zona con contenido único, datos verificables y enlazado interno. Nada de plantillas con la ciudad cambiada.",
        },
        {
          title: "Gestión de reseñas y respuestas",
          frequency: "Continua",
          description:
            "Solicitud ética a clientes reales en el momento adecuado y respuesta a todas las valoraciones, positivas y negativas, en menos de una hora. Sin comprar reseñas, nunca.",
        },
        {
          title: "Citaciones NAP y directorios sectoriales",
          frequency: "Continua",
          description:
            "Unificamos nombre, dirección y teléfono en directorios generalistas y verticales de tu sector. Las incoherencias de NAP frenan el posicionamiento local más de lo que la mayoría cree.",
        },
        {
          title: "Informe de llamadas, rutas y solicitudes",
          frequency: "Mensual",
          description:
            "Medimos lo que factura: llamadas desde la ficha, peticiones de ruta, clics a la web y formularios. Las impresiones son contexto, no resultado.",
        },
      ],
    },
    phases: {
      title: "Cómo trabajamos mes a mes",
      lead: "Compromiso mínimo de 3 meses, con entregables verificables y un único interlocutor senior. El mínimo no garantiza resultados en ese plazo; la continuidad posterior es opcional y a convenir.",
      items: [
        {
          title: "Diagnóstico y bloqueo de zona",
          timing: "Semana 0-1",
          description:
            "Analizamos tu situación, la de tus tres competidores más fuertes y comprobamos que tu sector está libre en tu zona. Si ya trabajamos con un competidor directo en tu área, te lo decimos y no seguimos.",
          outputs: ["Informe de diagnóstico en 48 h", "Análisis de 3 competidores locales", "Confirmación de exclusividad por sector y zona"],
        },
        {
          title: "Reconstrucción de base",
          timing: "Semana 1-4",
          description:
            "Ficha de empresa, NAP, estructura de páginas locales y arreglo de los bloqueos técnicos que impiden que Google entienda dónde operas y qué ofreces.",
          outputs: ["Ficha optimizada al 100%", "Primeras páginas de zona publicadas", "Corrección de citaciones incoherentes"],
        },
        {
          title: "Producción y captación de señales",
          timing: "Mes 2-3",
          description:
            "Publicamos contenido local nuevo cada mes, activamos el circuito de reseñas y trabajamos señales locales coherentes con los servicios y el área real del negocio.",
          outputs: ["Contenido local mensual", "Flujo continuo de reseñas reales", "Menciones y enlaces locales"],
        },
        {
          title: "Consolidación y expansión",
          timing: "Mes 4 en adelante · Continuidad opcional",
          description:
            "Tras el mínimo de 3 meses, si acordamos continuar, evaluamos los datos para ampliar a zonas colindantes y afinar lo que convierte. Esta fase no prolonga el compromiso mínimo ni garantiza posiciones o llamadas.",
          outputs: ["Expansión a zonas colindantes", "Informe mensual de llamadas y rutas", "Revisión trimestral de estrategia"],
        },
      ],
    },
    fit: {
      title: "SEO Local no es para todo el mundo",
      lead: "Solo aceptamos un cliente por sector y zona. Eso significa decir que no muchas más veces de las que decimos que sí.",
      yes: [
        "Tienes local físico o zona de servicio definida y quieres que suene el teléfono.",
        "Puedes sostener un proyecto continuado durante al menos 3 meses.",
        "Tu negocio depende de clientes de proximidad: salud, hostelería, servicios profesionales, reformas, inmobiliaria.",
        "Entiendes que el calendario de trabajo no fija un plazo garantizado de resultados.",
        "Quieres bloquear tu zona antes de que lo haga tu competencia.",
      ],
      no: [
        "Buscas una posición garantizada en un plazo cerrado: eso no existe y quien lo firma miente.",
        "Necesitas resultados esta semana; para eso está la publicidad SEM.",
        "Vendes solo online sin componente geográfico: tu servicio es SEO Ecommerce o SEO Web.",
        "Esperas comprar reseñas o inflar valoraciones.",
        "Prefieres un pack genérico cerrado en lugar de una estrategia adaptada a tu mercado.",
      ],
    },
    faqs: {
      title: "Preguntas que nos hacen antes de contratar",
      lead: "Respuestas directas, sin letra pequeña. Si falta alguna, te la resolvemos en el diagnóstico.",
      items: [
        {
          question: "¿Qué significa exactamente la exclusividad por sector y zona?",
          answer:
            "Que mientras trabajemos contigo no aceptamos a ningún competidor directo de tu mismo sector en tu zona. Queda recogido por contrato. Es la razón por la que limitamos el número de clientes: no podemos dar el mismo trabajo a dos negocios que compiten por las mismas búsquedas.",
        },
        {
          question: "¿Cuándo empiezo a notar más llamadas?",
          answer:
            "La evolución de visibilidad y llamadas depende de la situación inicial, la competencia y las decisiones de Google. Revisamos tareas, acciones de la ficha y solicitudes con datos disponibles. El compromiso mínimo de 3 meses no garantiza resultados en ese periodo; la continuidad posterior es opcional y a convenir.",
        },
        {
          question: "¿Necesito web para hacer SEO Local?",
          answer:
            "Se puede empezar por una ficha elegible de Google Business Profile. Una web accesible con información útil de servicio y zona puede apoyar la visibilidad y facilitar el contacto. Si no tienes web, valoramos su necesidad en el diagnóstico, sin garantizar mejoras.",
        },
        {
          question: "¿Qué pasa si tengo reseñas negativas?",
          answer:
            "Se responden. Rápido, con nombre y sin plantillas. Una respuesta profesional aporta contexto a quien lee la crítica y permite atender incidencias reales. Lo que no hacemos nunca es intentar eliminarlas de forma artificial ni compensarlas con reseñas compradas.",
        },
        {
          question: "¿Por qué no publicáis un precio cerrado?",
          answer:
            "Porque cada empresa necesita una estrategia propia. La inversión depende del número de zonas, la competencia y el volumen de contenido necesario. Tras el diagnóstico te presentamos una propuesta con alcance, prioridades e inversión concretos. Un precio cerrado sin ver tu caso solo sirve para vender paquetes que no encajan con nadie.",
        },
        {
          question: "¿Trabajáis en toda España?",
          answer:
            "Sí, con páginas de servicio para 20 zonas y prioridad en el anillo central. La condición es siempre la misma: que tu sector esté libre en tu zona. Puedes comprobar disponibilidad en el mapa de zonas exclusivas antes de solicitar el diagnóstico.",
        },
      ],
    },
    crossLinks: {
      title: "Servicios y recursos relacionados",
      lead: "El SEO Local rinde más combinado con visibilidad en motores de IA y con una web rápida detrás.",
      items: [
        { label: "Servicio", title: "GEO + SEO 360", description: "Visibilidad simultánea en Google y en ChatGPT, Gemini y Perplexity.", href: "/servicios/geo-seo-360" },
        { label: "Servicio", title: "Gestión de reseñas con IA", description: "El circuito ético que sostiene la reputación de tu ficha.", href: "/servicios/gestion-resenas-ia" },
        { label: "Zonas", title: "Mapa de zonas exclusivas", description: "Comprueba si tu sector sigue libre en tu zona.", href: "/zonas-exclusivas" },
        { label: "Guía", title: "SEO Local vs GEO", description: "Qué cambia cuando el cliente pregunta a una IA en vez de a Google.", href: "/seo-local-vs-geo-posicionamiento" },
      ],
    },
  },

  /* ------------------------------------------------------------------ */
  "seo-web-ia": {
    name: "SEO Web Avanzado con IA",
    path: "/servicios/seo-web-ia",
    description:
      "Posicionamiento orgánico nacional para webs de servicios y marcas. Propuesta personalizada según mercado, competencia, alcance y objetivos; compromiso mínimo 6 meses.",
    deliverables: {
      title: "Qué incluye el SEO Web Avanzado",
      lead: CTA_LEAD,
      items: [
        {
          title: "Auditoría técnica completa",
          frequency: "Inicio + mensual",
          description:
            "Rastreo total del dominio: indexación, canonicals, duplicidades, redirecciones, Core Web Vitals, renderizado JavaScript y arquitectura de URLs. Salimos con una lista priorizada por impacto, no con un PDF de 200 páginas.",
        },
        {
          title: "Investigación de palabras clave e intención",
          frequency: "Inicio + trimestral",
          description:
            "Clasificamos cada consulta por intención (informativa, comercial, transaccional) y la asignamos a una única URL. Así se evita la canibalización, que es lo que hunde a la mayoría de proyectos con mucho contenido.",
        },
        {
          title: "Arquitectura de contenidos y clusters",
          frequency: "Inicio",
          description:
            "Diseñamos la estructura de páginas pilar y artículos satélite con su enlazado interno. Cada pieza tiene un trabajo asignado dentro del conjunto: captar, sostener autoridad o convertir.",
        },
        {
          title: "Producción de contenido senior",
          frequency: "Mensual",
          description:
            "Contenido redactado con criterio experto y revisado por humanos, con datos, ejemplos y respuestas directas que las IA pueden citar. Sin relleno y sin texto generado sin supervisión.",
        },
        {
          title: "Optimización on-page y datos estructurados",
          frequency: "Continua",
          description:
            "Encabezados, metadatos, enlazado, imágenes y Schema.org por tipo de página. Los datos estructurados son hoy la vía más directa para aparecer en resultados enriquecidos y en respuestas generativas.",
        },
        {
          title: "Autoridad externa y seguimiento",
          frequency: "Mensual",
          description:
            "Enlaces editoriales seleccionados uno a uno y monitorización de posiciones, tráfico cualificado y conversiones. El informe mensual explica qué se hizo, qué movió y qué toca después.",
        },
      ],
    },
    phases: {
      title: "El proceso, fase a fase",
      lead: "Seis meses es el mínimo porque el SEO serio necesita ese tiempo para acumular señales. Estas son las fases y lo que sale de cada una.",
      items: [
        {
          title: "Diagnóstico y plan",
          timing: "Semana 0-2",
          description:
            "Auditoría técnica, análisis competitivo y mapa de oportunidades. Definimos qué se puede ganar, en cuánto tiempo y qué hace falta para conseguirlo.",
          outputs: ["Informe de diagnóstico en 48 h", "Mapa de palabras clave por intención", "Backlog priorizado por impacto"],
        },
        {
          title: "Corrección técnica",
          timing: "Mes 1-2",
          description:
            "Resolvemos lo que impide rastrear, indexar y renderizar correctamente. Sin esta base, cualquier contenido nuevo rinde a la mitad.",
          outputs: ["Errores críticos resueltos", "Core Web Vitals en verde", "Schema.org implementado"],
        },
        {
          title: "Producción y autoridad",
          timing: "Mes 2-6",
          description:
            "Publicación continua del calendario de contenidos, optimización de las páginas que ya tienen recorrido y construcción de enlaces editoriales.",
          outputs: ["Calendario editorial ejecutado", "Enlaces editoriales con justificación", "Optimización de páginas existentes"],
        },
        {
          title: "Escalado",
          timing: "Mes 6 en adelante",
          description:
            "Con datos reales, doblamos la apuesta en lo que funciona: nuevos clusters, expansión a long tail rentable y refuerzo de las URLs que ya generan negocio.",
          outputs: ["Nuevos clusters temáticos", "Informe de tráfico y conversión", "Revisión trimestral de estrategia"],
        },
      ],
    },
    fit: {
      title: "A quién aceptamos en SEO Web",
      lead: "Exclusividad por sector y zona: si trabajamos contigo, tu competidor directo no puede contratarnos. Por eso el filtro es estricto.",
      yes: [
        "Tu web ya tiene tráfico o producto validado y quieres multiplicarlo por vía orgánica.",
        "Puedes sostener un proyecto continuado durante 6 meses como mínimo.",
        "Tienes a alguien que pueda decidir y aprobar cambios sin tres semanas de reuniones.",
        "Compites en un sector donde el tráfico orgánico tiene valor claro por visita.",
        "Quieres que el trabajo se mida en clientes, no en impresiones.",
      ],
      no: [
        "Buscas garantía de posición: no la damos porque nadie puede darla honestamente.",
        "Necesitas facturar más el mes que viene: eso es SEM, no SEO.",
        "Tu negocio es puramente local de barrio: el servicio correcto es SEO Local.",
        "No puedes o no quieres publicar contenido nuevo con regularidad.",
        "Buscas el proveedor más barato del mercado. Somos lo contrario.",
      ],
    },
    faqs: {
      title: "Preguntas frecuentes sobre SEO Web",
      lead: "Lo que más nos preguntan las empresas antes de firmar.",
      items: [
        {
          question: "¿Cuánto tarda en dar resultados el SEO Web?",
          answer:
            "Los primeros movimientos en consultas de cola larga suelen aparecer entre el mes 2 y el 3, una vez corregida la base técnica. Las consultas competitivas necesitan de 4 a 8 meses de trabajo acumulado. Cualquier plazo más agresivo que eso es una promesa comercial, no una previsión técnica.",
        },
        {
          question: "¿Qué diferencia hay entre vuestro SEO y un servicio de bajo coste?",
          answer:
            "El volumen de trabajo real y quién lo ejecuta. Un servicio de bajo coste cubre informes automáticos y algún ajuste on-page. El nuestro incluye producción de contenido senior, corrección técnica, autoridad externa y un responsable con criterio revisando el proyecto cada semana.",
        },
        {
          question: "¿Usáis IA para escribir el contenido?",
          answer:
            "Usamos IA para investigar, analizar competencia y detectar oportunidades a escala. El contenido lo dirige y revisa una persona con conocimiento del sector. Publicar texto generado sin supervisión es la forma más rápida de perder autoridad tanto en Google como en los motores de IA.",
        },
        {
          question: "¿Podéis trabajar sobre WordPress, Shopify o React?",
          answer:
            "Sí. Estamos especializados en migraciones de WordPress y PrestaShop a React y Shopify, así que conocemos bien las tres realidades. Si tu plataforma actual limita el rendimiento de forma estructural, te lo decimos en el diagnóstico con datos, no como argumento de venta.",
        },
        {
          question: "¿Qué pasa si dejo el servicio a los 6 meses?",
          answer:
            "Todo el trabajo se queda contigo: contenido, correcciones técnicas, datos estructurados y enlaces. No usamos sistemas que dejen de funcionar al irte. Lo que sí ocurre es que la competencia sigue trabajando, y la ventaja acumulada se erosiona con los meses.",
        },
        {
          question: "¿Incluye SEO y GEO para motores de IA?",
          answer:
            "El SEO Web incluye datos estructurados y contenido con formato de respuesta directa, que ya favorece la citación en IA. Si quieres un trabajo específico de visibilidad en ChatGPT, Gemini y Perplexity, con monitorización propia, ese es el servicio GEO + SEO 360.",
        },
      ],
    },
    crossLinks: {
      title: "Continúa por aquí",
      lead: "Rutas habituales de quien llega a esta página.",
      items: [
        { label: "Servicio", title: "GEO + SEO 360", description: "Añade visibilidad en motores de IA a tu estrategia orgánica.", href: "/servicios/geo-seo-360" },
        { label: "Servicio", title: "Link Building inteligente", description: "Autoridad externa con enlaces editoriales justificados uno a uno.", href: "/servicios/link-building-inteligente" },
        { label: "Servicio", title: "Web en React", description: "Cuando la plataforma es el cuello de botella del posicionamiento.", href: "/servicios/creacion-web-react" },
        { label: "Pilar", title: "Posicionamiento web", description: "La guía completa de cómo abordamos el posicionamiento orgánico.", href: "/posicionamiento-web" },
      ],
    },
  },

  /* ------------------------------------------------------------------ */
  "seo-ecommerce-ia": {
    name: "SEO Ecommerce con IA",
    path: "/servicios/seo-ecommerce-ia",
    description:
      "Posicionamiento orgánico para tiendas online: categorías, fichas, facetas e inventario. Propuesta personalizada según mercado, competencia, alcance y objetivos; compromiso mínimo 6 meses.",
    deliverables: {
      title: "Qué incluye el SEO para ecommerce",
      lead: CTA_LEAD,
      items: [
        {
          title: "Arquitectura de categorías y facetas",
          frequency: "Inicio",
          description:
            "Definimos qué categorías, subcategorías y filtros deben ser indexables y cuáles no. El control de la navegación facetada es la diferencia entre un catálogo que posiciona y uno que se ahoga en URLs duplicadas.",
        },
        {
          title: "Optimización masiva de fichas de producto",
          frequency: "Continua",
          description:
            "Títulos, descripciones únicas, atributos, imágenes con texto alternativo y Schema Product con precio, stock y valoraciones. Trabajamos por plantillas inteligentes y revisión manual de los productos que más facturan.",
        },
        {
          title: "Contenido de categoría con intención comercial",
          frequency: "Mensual",
          description:
            "Textos de categoría que responden a la duda real antes de comprar: comparativas, guías de talla o compatibilidad, criterios de elección. Es el contenido que más convierte y el que casi nadie trabaja.",
        },
        {
          title: "Gestión de catálogo vivo",
          frequency: "Continua",
          description:
            "Protocolo para productos agotados, descatalogados y estacionales: qué se redirige, qué se mantiene y qué se despublica. Un catálogo mal gestionado destruye autoridad acumulada cada temporada.",
        },
        {
          title: "Rendimiento y Core Web Vitals",
          frequency: "Mensual",
          description:
            "Optimización de imágenes, carga diferida, tiempos de respuesta y experiencia móvil. En ecommerce cada décima de carga se traduce directamente en tasa de conversión.",
        },
        {
          title: "Informe de ingresos por canal orgánico",
          frequency: "Mensual",
          description:
            "Medimos ingresos, ticket medio y productos que entran por búsqueda orgánica. Las sesiones sin facturación asociada no son un resultado.",
        },
      ],
    },
    phases: {
      title: "Cómo desplegamos el proyecto",
      lead: "En ecommerce el orden importa: primero se deja de perder, después se empieza a ganar.",
      items: [
        {
          title: "Auditoría de catálogo e indexación",
          timing: "Semana 0-2",
          description:
            "Rastreo completo, análisis de qué está indexado y qué no debería estarlo, y detección de canibalización entre categorías y fichas.",
          outputs: ["Informe de diagnóstico en 48 h", "Mapa de indexación del catálogo", "Lista de duplicidades críticas"],
        },
        {
          title: "Saneamiento técnico",
          timing: "Mes 1-2",
          description:
            "Control de facetas, canonicals, paginación, redirecciones de productos caídos y rendimiento. Aquí se recupera el presupuesto de rastreo que estabas desperdiciando.",
          outputs: ["Facetas bajo control", "Redirecciones de catálogo resueltas", "Core Web Vitals optimizados"],
        },
        {
          title: "Optimización comercial",
          timing: "Mes 2-5",
          description:
            "Trabajo fino sobre las categorías y productos con mayor potencial de facturación, más contenido comercial y datos estructurados completos.",
          outputs: ["Categorías top optimizadas", "Fichas prioritarias reescritas", "Schema Product completo"],
        },
        {
          title: "Escalado y estacionalidad",
          timing: "Mes 5 en adelante",
          description:
            "Planificamos con antelación campañas estacionales, ampliamos a long tail de producto y reforzamos la autoridad del dominio.",
          outputs: ["Calendario estacional", "Expansión a long tail de producto", "Informe de ingresos orgánicos"],
        },
      ],
    },
    fit: {
      title: "Requisitos para entrar",
      lead: "Un solo cliente por sector. En ecommerce esa exclusividad vale más que en cualquier otro servicio.",
      yes: [
        "Facturas ya online y quieres reducir tu dependencia de la publicidad de pago.",
        "Puedes sostener un proyecto continuado durante al menos 6 meses.",
        "Tienes catálogo propio o acuerdos que te diferencian de la competencia.",
        "Alguien de tu equipo puede aplicar cambios en la tienda con agilidad.",
        "Tu margen soporta invertir hoy para facturar más en 6-12 meses.",
      ],
      no: [
        "Revendes exactamente el mismo catálogo que otros veinte con las mismas descripciones del fabricante.",
        "Tu único objetivo es vender esta semana: eso es Google Shopping y SEM.",
        "No puedes tocar la plantilla ni el sistema de filtros de tu tienda.",
        "Esperas una posición garantizada para tus productos estrella.",
        "Buscas un paquete cerrado sin análisis previo de tu catálogo y tu competencia.",
      ],
    },
    faqs: {
      title: "Preguntas frecuentes sobre SEO Ecommerce",
      lead: "Las dudas que aparecen siempre en la primera reunión.",
      items: [
        {
          question: "¿Merece la pena hacer SEO si ya invierto en Google Ads?",
          answer:
            "Sí, y suelen potenciarse. Ads te da tráfico mientras pagas; el orgánico construye un activo que sigue facturando cuando bajas la inversión. Lo habitual es que el SEO reduzca la dependencia de Ads en 6-12 meses y baje el coste medio de adquisición del conjunto.",
        },
        {
          question: "¿Qué hago con los productos agotados o descatalogados?",
          answer:
            "Depende. Si vuelve a haber stock, se mantiene la URL con aviso de disponibilidad. Si desaparece definitivamente, se redirige al producto sustituto o a su categoría. Eliminar sin redirigir es la forma más habitual de perder posiciones acumuladas durante años en una sola limpieza de catálogo.",
        },
        {
          question: "¿Trabajáis con Shopify, PrestaShop o WooCommerce?",
          answer:
            "Con las tres, y también con desarrollos a medida. Estamos especializados en migraciones desde PrestaShop y WooCommerce hacia Shopify cuando la plataforma actual limita el crecimiento. Si migrar no aporta, te lo decimos: una migración mal planteada cuesta meses de visibilidad.",
        },
        {
          question: "¿Cómo evitáis el contenido duplicado en miles de fichas?",
          answer:
            "Con plantillas inteligentes que combinan atributos reales del producto y redacción manual en las fichas que concentran la facturación. Además controlamos canonicals y facetas para que Google vea una sola versión de cada producto, no ocho variantes de la misma URL.",
        },
        {
          question: "¿En cuánto tiempo se nota en la facturación?",
          answer:
            "El saneamiento técnico suele dar mejoras visibles entre el mes 2 y el 3. El impacto claro en ingresos orgánicos aparece habitualmente entre el mes 4 y el 8, según el tamaño del catálogo y la competencia del sector. Por eso el compromiso mínimo es de 6 meses.",
        },
        {
          question: "¿Incluye la gestión del feed de Google Shopping?",
          answer:
            "No, ese es el servicio de Google Merchant con IA. Se contratan por separado porque son trabajos distintos, aunque se coordinan bien: el mismo catálogo optimizado alimenta tanto el orgánico como el feed de Shopping y mejora ambos a la vez.",
        },
      ],
    },
    crossLinks: {
      title: "Servicios complementarios",
      lead: "Lo que suelen contratar junto al SEO Ecommerce.",
      items: [
        { label: "Servicio", title: "Google Merchant con IA", description: "Feed optimizado y pujas ajustadas en Shopping.", href: "/servicios/google-merchant-ia" },
        { label: "Servicio", title: "Creación de ecommerce Shopify", description: "Cuando la tienda actual es el techo del proyecto.", href: "/servicios/creacion-ecommerce-shopify" },
        { label: "Servicio", title: "Publicidad SEM con IA", description: "Tráfico inmediato mientras el orgánico madura.", href: "/servicios/publicidad-sem-ia" },
        { label: "Casos", title: "Casos de éxito", description: "Resultados reales, con métricas verificables.", href: "/casos-exito" },
      ],
    },
  },

  /* ------------------------------------------------------------------ */
  "geo-seo-360": {
    name: "GEO + SEO 360",
    path: "/servicios/geo-seo-360",
    description:
      "Visibilidad simultánea en Google y en motores de IA generativa: ChatGPT, Gemini, Perplexity y Copilot. Propuesta personalizada según mercado, competencia, alcance y objetivos; compromiso mínimo 6 meses y plazas limitadas.",
    deliverables: {
      title: "Qué incluye GEO + SEO 360",
      lead: CTA_LEAD,
      items: [
        {
          title: "Auditoría de presencia en motores de IA",
          frequency: "Inicio + mensual",
          description:
            "Lanzamos baterías de consultas reales en ChatGPT, Gemini, Perplexity y Copilot para ver si te citan, cómo te describen y a qué competidor mencionan en tu lugar. Ese es el punto de partida medible.",
        },
        {
          title: "Contenido con formato de respuesta directa",
          frequency: "Mensual",
          description:
            "Bloques de 50-80 palabras que responden la pregunta antes de desarrollarla, con datos verificables y fecha de actualización. La extensión se adapta a la pregunta: 50-80 palabras es una pauta editorial, no un requisito ni una garantía de citación.",
        },
        {
          title: "Datos estructurados avanzados",
          frequency: "Continua",
          description:
            "Schema.org completo y coherente: Organization, Service, FAQPage, LocalBusiness y Article donde corresponda. El marcado describe contenido visible; Google no exige un schema especial para AI Overviews ni AI Mode.",
        },
        {
          title: "Autoridad en fuentes que las IA leen",
          frequency: "Mensual",
          description:
            "Presencia en los medios, directorios y comunidades que los modelos usan como referencia para tu sector. Aparecer en la fuente correcta pesa más que veinte enlaces irrelevantes.",
        },
        {
          title: "Todo el SEO Web Avanzado incluido",
          frequency: "Continua",
          description:
            "Auditoría técnica, contenido, enlazado, Core Web Vitals y seguimiento. El acceso técnico y el contenido útil son la base compartida; los motores pueden usar fuentes y modos de búsqueda distintos.",
        },
        {
          title: "Informe de citación en IA",
          frequency: "Mensual",
          description:
            "Seguimiento de en qué consultas apareces, con qué descripción y frente a qué competidores, motor por motor. La muestra, condiciones y fuentes se documentan; no representa una cuota de mercado.",
        },
      ],
    },
    phases: {
      title: "Los primeros 6 meses y la continuidad",
      lead: "El compromiso mínimo es de 6 meses: diagnóstico, base técnica, contenido y evaluación. No garantiza resultados en ese plazo. La continuidad posterior es opcional y a convenir, sin prolongar el mínimo acordado.",
      items: [
        {
          title: "Línea base de visibilidad",
          timing: "Mes 1",
          description:
            "Medimos tu presencia actual en cada motor de IA y en Google, y documentamos qué se dice hoy de tu marca. Sin línea base no hay forma honesta de demostrar progreso.",
          outputs: ["Informe de citación inicial", "Batería de consultas de control", "Auditoría técnica completa"],
        },
        {
          title: "Base técnica y semántica",
          timing: "Mes 1-3",
          description:
            "Datos estructurados, arquitectura de entidades, coherencia de marca en todas las fuentes y corrección de lo que impide un rastreo limpio.",
          outputs: ["Schema.org desplegado", "Entidad de marca consolidada", "Errores técnicos resueltos"],
        },
        {
          title: "Producción de contenido citable",
          timing: "Mes 3-6",
          description:
            "Publicamos información útil y contrastable: respuestas claras, datos propios autorizados, comparativas honestas y páginas de referencia. Observamos su evolución sin garantizar citas.",
          outputs: ["Corpus de contenido citable", "Presencia en fuentes de referencia", "Informe mensual de citación"],
        },
        {
          title: "Evaluación y continuidad opcional",
          timing: "Mes 6 · Evaluación; después, a convenir",
          description:
            "Al cierre del sexto mes evaluamos el trabajo y la evolución de visibilidad. Si acordamos continuar, refinamos el contenido y ampliamos consultas según los datos; esa continuidad no prolonga el compromiso mínimo.",
          outputs: ["Expansión a consultas conversacionales", "Comparativa frente a competidores", "Propuesta de continuidad a convenir"],
        },
      ],
    },
    fit: {
      title: "Plazas limitadas en toda España",
      lead: "Es el servicio más exigente que ofrecemos y lo ejecuta únicamente el equipo senior. El filtro es deliberadamente duro.",
      yes: [
        "Ya tienes presencia consolidada en Google y quieres el siguiente terreno antes que tu competencia.",
        "Puedes sostener un proyecto continuado durante al menos 6 meses.",
        "Tu sector ya recibe consultas complejas que la gente hace a una IA antes de decidir.",
        "Entiendes que las respuestas varían y se evalúan con una muestra y revisiones periódicas.",
        "Tienes datos, experiencia o criterio propio que merezca ser citado como fuente.",
      ],
      no: [
        "Tu web todavía no funciona bien en Google: primero SEO Web, después GEO.",
        "Buscas resultados en 90 días.",
        "No puedes comprometer un mínimo de 6 meses.",
        "Esperas que garanticemos aparecer en ChatGPT: podemos ejecutar y documentar el trabajo acordado, no firmar el resultado de un modelo que no controlamos.",
        "Las plazas están ocupadas: en ese caso entras en lista de espera.",
      ],
    },
    faqs: {
      title: "Preguntas frecuentes sobre GEO",
      lead: "GEO es nuevo y genera dudas legítimas. Estas son las más habituales.",
      items: [
        {
          question: "¿Qué es exactamente el GEO?",
          answer:
            "GEO (Generative Engine Optimization) orienta el trabajo a la presencia en respuestas generativas: información de empresa, contenido útil, fuentes y observación de menciones y citas. Se apoya en una base SEO y no equivale a SEO local ni garantiza que un motor recomiende tu marca.",
        },
        {
          question: "¿Se puede medir la visibilidad en ChatGPT o Gemini?",
          answer:
            "Sí, aunque no como en Google. Lanzamos baterías fijas de consultas cada mes en cada motor y registramos si apareces, con qué descripción y junto a qué competidores. No es un ranking exacto, pero es una serie temporal comparable que muestra la evolución real.",
        },
        {
          question: "¿Por qué 6 meses de compromiso?",
          answer:
            "El mínimo de 6 meses permite organizar el diagnóstico, corregir la base técnica, publicar contenido y evaluar su evolución con mediciones periódicas. No equivale a un plazo garantizado de citación ni depende de reentrenar modelos que no controlamos. Al finalizar evaluamos lo ejecutado y la visibilidad observada; cualquier continuidad es opcional y a convenir.",
        },
        {
          question: "¿No basta con hacer buen SEO tradicional?",
          answer:
            "Las bases SEO siguen siendo relevantes. Google indica que AI Overviews y AI Mode no requieren optimizaciones, archivos ni schema especiales. Añadimos revisión de información y observación de respuestas para el contexto de tu empresa; no existe una receta universal ni garantía de indexación o citación.",
        },
        {
          question: "¿Qué pasa si las plazas están ocupadas?",
          answer:
            "Te lo decimos en el primer contacto y entras en lista de espera con prioridad por orden de solicitud. Mientras tanto, valoramos si necesitas trabajar la base con SEO Web Avanzado; no implica contratarlo como requisito ni garantiza acelerar las respuestas de IA.",
        },
        {
          question: "¿Garantizáis aparecer en las respuestas de IA?",
          answer:
            "No. Nadie puede garantizar el comportamiento de un modelo que no controla, igual que nadie puede garantizar una posición en Google. Lo que sí garantizamos es ejecutar íntegramente el trabajo profesional acordado, documentar los cambios, y medirlo cada mes.",
        },
      ],
    },
    crossLinks: {
      title: "Antes de contratar GEO",
      lead: "Contexto útil para decidir si este es tu servicio.",
      items: [
        { label: "Guía", title: "SEO Local vs GEO", description: "Cuándo tiene sentido cada uno y cómo se solapan.", href: "/seo-local-vs-geo-posicionamiento" },
        { label: "Servicio", title: "SEO Web Avanzado", description: "Trabajo técnico, contenido y autoridad como base SEO.", href: "/servicios/seo-web-ia" },
        { label: "Guía", title: "Cuál es la mejor agencia SEO con IA", description: "Criterios objetivos para comparar proveedores.", href: "/cual-mejor-agencia-seo-ia-espana" },
        { label: "Blog", title: "Blog de SEO y GEO", description: "Análisis actualizados sobre búsqueda generativa.", href: "/blog" },
      ],
    },
  },

  /* ------------------------------------------------------------------ */
  "publicidad-sem-ia": {
    name: "Publicidad SEM con IA",
    path: "/servicios/publicidad-sem-ia",
    description:
      "Gestión de Google Ads con optimización mediante IA. Honorarios según alcance; inversión publicitaria aparte, en tu propia cuenta.",
    deliverables: {
      title: "Qué incluye la gestión SEM",
      lead: CTA_LEAD,
      items: [
        {
          title: "Auditoría de cuenta y estructura",
          frequency: "Inicio",
          description:
            "Revisión de campañas, grupos, concordancias, negativas, conversiones mal configuradas y atribución. La mayoría de cuentas pierden entre un 20% y un 40% del presupuesto por problemas de estructura.",
        },
        {
          title: "Reconstrucción de campañas",
          frequency: "Inicio",
          description:
            "Estructura por intención y rentabilidad, no por catálogo. Separamos marca, genéricas y competencia para que el presupuesto se pueda gobernar de verdad.",
        },
        {
          title: "Medición y conversiones",
          frequency: "Inicio + continua",
          description:
            "Configuración de conversiones reales con valor asociado, integración con GA4 y consentimiento correcto. Sin medición fiable, optimizar es apostar.",
        },
        {
          title: "Optimización asistida por IA",
          frequency: "Diaria",
          description:
            "Ajustes de pujas por hora, dispositivo y ubicación; detección de términos que queman presupuesto; alertas sobre caídas de rendimiento antes de que se noten en la facturación.",
        },
        {
          title: "Creatividades y test continuo",
          frequency: "Quincenal",
          description:
            "Anuncios nuevos en rotación, extensiones completas y pruebas sobre las páginas de destino. El anuncio y la landing se optimizan juntos o no se optimiza nada.",
        },
        {
          title: "Informe de coste por adquisición",
          frequency: "Mensual",
          description:
            "Inversión, conversiones, coste por adquisición y retorno por campaña. Todo con el detalle de qué se cambió ese mes y qué efecto tuvo.",
        },
      ],
    },
    phases: {
      title: "Los primeros 90 días",
      lead: "Tres meses de compromiso mínimo porque la IA necesita datos acumulados para optimizar con criterio.",
      items: [
        {
          title: "Auditoría y medición",
          timing: "Semana 1-2",
          description:
            "Diagnóstico de la cuenta actual y reparación del sistema de medición. Empezamos por saber qué está pasando de verdad.",
          outputs: ["Informe de auditoría en 48 h", "Conversiones con valor configuradas", "Lista de fugas de presupuesto"],
        },
        {
          title: "Reestructuración",
          timing: "Semana 2-4",
          description:
            "Nueva estructura de campañas, negativas, concordancias y creatividades. Aquí suele producirse la primera caída de coste por adquisición.",
          outputs: ["Campañas reconstruidas", "Listado de negativas aplicado", "Nuevos anuncios en rotación"],
        },
        {
          title: "Optimización con datos",
          timing: "Mes 2-3",
          description:
            "Con volumen suficiente, la IA ajusta pujas y reparto de presupuesto entre campañas según rentabilidad real, no según impresiones.",
          outputs: ["Pujas optimizadas a diario", "Test A/B de landings", "Detección de clics inválidos"],
        },
        {
          title: "Escalado rentable",
          timing: "Mes 3 en adelante",
          description:
            "Ampliamos inversión solo donde el retorno lo justifica y abrimos nuevos canales (Shopping, Performance Max, remarketing) cuando el dato lo respalda.",
          outputs: ["Plan de escalado por campaña", "Nuevos canales activados", "Informe mensual de retorno"],
        },
      ],
    },
    fit: {
      title: "Requisitos para gestionar tu cuenta",
      lead: "No aceptamos cuentas pequeñas: por debajo de cierto volumen la optimización con IA no tiene datos con los que trabajar.",
      yes: [
        "Tienes o quieres activar campañas con volumen suficiente para optimizar con datos.",
        "Entiendes que los honorarios de gestión van aparte de la inversión publicitaria.",
        "Tienes un producto o servicio con margen suficiente para pagar tráfico.",
        "Aceptas que toquemos también las páginas de destino: es la mitad del resultado.",
        "Quieres medir coste por cliente, no coste por clic.",
      ],
      no: [
        "Tu presupuesto publicitario no permite reunir datos suficientes; en ese caso te recomendaremos otra vía.",
        "Buscas una comisión del 10% sobre inversión sin trabajo estratégico detrás.",
        "No puedes modificar tu web ni tus formularios.",
        "Esperas rentabilidad la primera semana: los primeros datos útiles llegan entre el día 7 y el 15.",
        "Quieres publicidad para compensar un producto que no convierte.",
      ],
    },
    faqs: {
      title: "Preguntas frecuentes sobre SEM",
      lead: "Lo que conviene aclarar antes de mover un euro de inversión.",
      items: [
        {
          question: "¿Los honorarios incluyen la inversión publicitaria?",
          answer:
            "No. Los honorarios cubren gestión y estrategia y se concretan en la propuesta tras el diagnóstico. La inversión en Google Ads la pagas directamente a Google desde tu propia cuenta, que siempre es tuya. En el diagnóstico estimamos qué volumen necesita tu mercado para que la optimización tenga datos suficientes.",
        },
        {
          question: "¿La cuenta de Google Ads es mía?",
          answer:
            "Siempre. Trabajamos sobre tu cuenta con acceso de gestor, nunca desde una cuenta agencia compartida. Si algún día terminamos la colaboración, conservas el histórico completo, las conversiones configuradas y el aprendizaje acumulado de las campañas.",
        },
        {
          question: "¿SEM o SEO, qué me conviene primero?",
          answer:
            "SEM si necesitas facturar ya y tienes margen para pagar tráfico. SEO si buscas un activo que reduzca el coste de adquisición a medio plazo. Lo más rentable suele ser empezar con ambos: Ads sostiene la caja mientras el orgánico madura y luego se reequilibra el reparto.",
        },
        {
          question: "¿Cómo evitáis el gasto en clics inútiles?",
          answer:
            "Con listados de negativas revisados cada semana, control de concordancias, exclusión de ubicaciones y dispositivos que no convierten, y detección de patrones de clic inválido. En cuentas mal estructuradas es donde más rápido se recupera presupuesto: a veces un tercio del gasto mensual.",
        },
        {
          question: "¿Trabajáis Performance Max y Shopping?",
          answer:
            "Sí, cuando el caso lo justifica. Performance Max funciona bien con medición sólida y feed cuidado, y mal cuando se usa como atajo para no estructurar la cuenta. Si vendes producto, la combinación con Google Merchant optimizado suele ser la palanca más rentable.",
        },
        {
          question: "¿Por qué no cobráis un porcentaje de la inversión?",
          answer:
            "Porque ese modelo premia gastar más, no ganar más. Con honorarios fijos nuestro incentivo coincide con el tuyo: bajar el coste por adquisición aunque eso signifique recomendarte reducir inversión en campañas que no rinden.",
        },
      ],
    },
    crossLinks: {
      title: "Servicios relacionados",
      lead: "Lo que mejor combina con una cuenta de Ads bien gestionada.",
      items: [
        { label: "Servicio", title: "Google Merchant con IA", description: "Feed limpio y competitivo para Shopping y PMax.", href: "/servicios/google-merchant-ia" },
        { label: "Servicio", title: "SEO Web Avanzado", description: "Reduce a medio plazo tu dependencia del tráfico pagado.", href: "/servicios/seo-web-ia" },
        { label: "Servicio", title: "Web en React", description: "Landings rápidas: la mitad del rendimiento de una campaña.", href: "/servicios/creacion-web-react" },
        { label: "Contacto", title: "Diagnóstico de tu empresa", description: "Auditoría de tu cuenta actual en 48 horas.", href: "/agenda-consultoria" },
      ],
    },
  },

  /* ------------------------------------------------------------------ */
  "google-merchant-ia": {
    name: "Google Merchant Center con IA",
    path: "/servicios/google-merchant-ia",
    description:
      "Optimización del feed de productos y de Google Shopping con IA. Propuesta personalizada según mercado, competencia, alcance y objetivos; compromiso mínimo 3 meses.",
    deliverables: {
      title: "Qué incluye la gestión de Merchant Center",
      lead: CTA_LEAD,
      items: [
        {
          title: "Diagnóstico y desbloqueo de cuenta",
          frequency: "Inicio",
          description:
            "Revisión de suspensiones, avisos y desaprobaciones de producto, y de las políticas que las provocan. Una cuenta con productos desaprobados está perdiendo ventas todos los días en silencio.",
        },
        {
          title: "Reconstrucción del feed",
          frequency: "Inicio + continua",
          description:
            "Títulos con la estructura que Google entiende, atributos completos, GTIN, categorización correcta y descripciones útiles. El título del producto es el factor con más impacto en Shopping.",
        },
        {
          title: "Reglas de feed automatizadas",
          frequency: "Continua",
          description:
            "Transformaciones automáticas para mantener el feed correcto sin tocar la tienda cada semana: normalización de marcas, tallas, colores y disponibilidad.",
        },
        {
          title: "Segmentación por rentabilidad",
          frequency: "Mensual",
          description:
            "Etiquetas personalizadas por margen, rotación y estacionalidad para que las pujas se puedan gobernar por producto y no por catálogo entero.",
        },
        {
          title: "Análisis competitivo de precios",
          frequency: "Mensual",
          description:
            "Comparativa de tu posicionamiento de precio frente a los vendedores que compiten por las mismas fichas, con recomendaciones sobre dónde puedes ganar y dónde no vale la pena pujar.",
        },
        {
          title: "Informe de rendimiento por producto",
          frequency: "Mensual",
          description:
            "Qué productos generan impresiones, cuáles convierten y cuáles solo consumen presupuesto. Con decisiones concretas para el mes siguiente.",
        },
      ],
    },
    phases: {
      title: "El plan de trabajo",
      lead: "Tres meses de compromiso: el primero se dedica casi entero a dejar el feed impecable.",
      items: [
        {
          title: "Auditoría del feed",
          timing: "Semana 1",
          description:
            "Revisamos cada error, aviso y desaprobación, y cuantificamos cuántos productos están fuera de circulación y qué facturación representan.",
          outputs: ["Informe de auditoría en 48 h", "Listado de productos desaprobados", "Plan de corrección priorizado"],
        },
        {
          title: "Saneamiento",
          timing: "Semana 2-4",
          description:
            "Corrección de atributos, títulos y categorías, y despliegue de reglas de feed para que el problema no vuelva el mes siguiente.",
          outputs: ["Catálogo aprobado al máximo posible", "Títulos reestructurados", "Reglas automatizadas activas"],
        },
        {
          title: "Optimización comercial",
          timing: "Mes 2",
          description:
            "Segmentación por rentabilidad, ajuste de pujas por grupo de producto y trabajo sobre las fichas con mejor potencial.",
          outputs: ["Etiquetas personalizadas", "Pujas por segmento", "Análisis competitivo de precios"],
        },
        {
          title: "Rendimiento sostenido",
          timing: "Mes 3 en adelante",
          description:
            "Monitorización continua, control de estacionalidad y expansión a nuevos formatos cuando los datos lo respaldan.",
          outputs: ["Monitorización diaria del feed", "Calendario estacional", "Informe mensual por producto"],
        },
      ],
    },
    fit: {
      title: "Cuándo tiene sentido este servicio",
      lead: "Merchant Center es un servicio muy concreto. Estos son los casos en los que rinde y en los que no.",
      yes: [
        "Vendes producto físico online y ya usas o quieres usar Google Shopping.",
        "Puedes sostener un proyecto continuado durante al menos 3 meses.",
        "Tu catálogo tiene al menos varias decenas de referencias.",
        "Tienes o puedes generar un feed conectado a tu tienda.",
        "Te interesa que el mismo trabajo mejore también Performance Max.",
      ],
      no: [
        "Vendes servicios o productos digitales sin ficha de producto.",
        "Tu catálogo tiene cuatro referencias: no hay volumen que optimizar.",
        "No tienes acceso a la tienda ni a quien la gestiona.",
        "Buscas solo que alguien conecte el feed una vez y desaparezca.",
        "No inviertes ni piensas invertir en campañas de Shopping.",
      ],
    },
    faqs: {
      title: "Preguntas frecuentes sobre Google Merchant",
      lead: "Las dudas típicas de tiendas que llegan con la cuenta bloqueada o el feed lleno de avisos.",
      items: [
        {
          question: "Tengo la cuenta de Merchant suspendida, ¿podéis ayudarme?",
          answer:
            "Sí, es uno de los motivos más frecuentes por los que nos llaman. Identificamos la política incumplida, corregimos la causa real (normalmente datos de contacto, política de devoluciones, precios incoherentes o productos prohibidos) y gestionamos la solicitud de revisión con la documentación necesaria.",
        },
        {
          question: "¿Cuánto influye el título del producto?",
          answer:
            "Mucho. Es la señal principal que usa Google para decidir a qué búsquedas mostrar tu ficha. Un título estructurado con marca, modelo, atributo clave y variante rinde muy por encima del nombre comercial que suele venir del catálogo del fabricante. Es lo primero que reconstruimos.",
        },
        {
          question: "¿Necesito también campañas de Ads gestionadas por vosotros?",
          answer:
            "No es obligatorio: podemos optimizar solo el feed mientras otro equipo lleva las campañas. Dicho esto, el resultado es notablemente mejor cuando feed y pujas se coordinan, porque la segmentación por rentabilidad que creamos solo sirve si alguien la usa en la cuenta de Ads.",
        },
        {
          question: "¿Cada cuánto se actualiza el feed?",
          answer:
            "Configuramos actualizaciones diarias y, cuando la plataforma lo permite, actualizaciones a tiempo real de precio y disponibilidad. Anunciar un producto agotado o con precio antiguo genera desaprobaciones, empeora la experiencia y acaba costando visibilidad en toda la cuenta.",
        },
        {
          question: "¿Funciona con Shopify y PrestaShop?",
          answer:
            "Sí, y también con WooCommerce y desarrollos propios. Trabajamos sobre el feed generado por la plataforma y aplicamos reglas en Merchant Center para corregir lo que la tienda no puede resolver sin desarrollo, de modo que no dependas de tu equipo técnico para cada ajuste.",
        },
        {
          question: "¿En cuánto tiempo se ven resultados?",
          answer:
            "El saneamiento del feed suele reflejarse en 2-4 semanas: más productos aprobados significa más impresiones inmediatas. La mejora sostenida en coste por conversión aparece habitualmente en el segundo o tercer mes, cuando la segmentación por rentabilidad ya tiene datos con los que trabajar.",
        },
      ],
    },
    crossLinks: {
      title: "Continuar por aquí",
      lead: "Servicios que se apoyan en un catálogo bien trabajado.",
      items: [
        { label: "Servicio", title: "Publicidad SEM con IA", description: "Quien gobierna las pujas que usan tu feed.", href: "/servicios/publicidad-sem-ia" },
        { label: "Servicio", title: "SEO Ecommerce con IA", description: "El mismo catálogo, trabajado para el canal orgánico.", href: "/servicios/seo-ecommerce-ia" },
        { label: "Servicio", title: "Creación de ecommerce Shopify", description: "Cuando la plataforma limita lo que se puede optimizar.", href: "/servicios/creacion-ecommerce-shopify" },
        { label: "Contacto", title: "Diagnóstico de tu empresa", description: "Auditoría de tu feed actual en 48 horas.", href: "/agenda-consultoria" },
      ],
    },
  },

  /* ------------------------------------------------------------------ */
  "link-building-inteligente": {
    name: "Link Building inteligente",
    path: "/servicios/link-building-inteligente",
    description:
      "Autoridad externa con enlaces editoriales seleccionados uno a uno. Propuesta personalizada según mercado, competencia, alcance y objetivos; compromiso mínimo 3 meses.",
    deliverables: {
      title: "Qué incluye el servicio de autoridad",
      lead: CTA_LEAD,
      items: [
        {
          title: "Auditoría del perfil de enlaces",
          frequency: "Inicio",
          description:
            "Analizamos qué enlaces tienes, cuáles te ayudan y cuáles son un riesgo heredado de proveedores anteriores. Cuando hace falta, preparamos el archivo de desautorización.",
        },
        {
          title: "Análisis de brecha frente a competidores",
          frequency: "Inicio + trimestral",
          description:
            "Identificamos los dominios que enlazan a tus tres competidores más fuertes y a ti no. Ese listado es la hoja de ruta: sabemos exactamente qué autoridad falta para competir.",
        },
        {
          title: "Prospección manual y cualificación",
          frequency: "Mensual",
          description:
            "Cada dominio candidato se revisa a mano: tráfico real, relevancia temática, perfil de salida y si publica contenido patrocinado en masa. Rechazamos más dominios de los que aprobamos.",
        },
        {
          title: "Contenido editorial de calidad",
          frequency: "Mensual",
          description:
            "Redactamos piezas que el medio quiere publicar por su valor, no solo por el pago. Un enlace dentro de contenido que se lee vale más que diez en secciones muertas.",
        },
        {
          title: "Diversificación de anchors y destinos",
          frequency: "Continua",
          description:
            "Repartimos texto ancla y páginas de destino con un perfil natural. La sobreoptimización del ancla sigue siendo una de las causas más frecuentes de pérdida repentina de visibilidad.",
        },
        {
          title: "Informe con justificación por enlace",
          frequency: "Mensual",
          description:
            "Cada enlace conseguido llega con URL, métricas del dominio, motivo de selección y página de destino. Sin listados opacos ni 'paquetes de 50 enlaces'.",
        },
      ],
    },
    phases: {
      title: "Cómo construimos autoridad",
      lead: "Tres meses mínimo porque un perfil de enlaces creíble se construye con ritmo constante, no con picos.",
      items: [
        {
          title: "Diagnóstico del perfil",
          timing: "Semana 1-2",
          description:
            "Auditoría de enlaces existentes, detección de riesgos y análisis de la brecha real frente a los competidores que ocupan el terreno que quieres.",
          outputs: ["Informe de perfil de enlaces", "Archivo de desautorización si procede", "Listado de brecha competitiva"],
        },
        {
          title: "Estrategia y prospección",
          timing: "Semana 2-4",
          description:
            "Definimos páginas objetivo, reparto de anclas y ritmo mensual, y empezamos a cualificar dominios candidatos uno a uno.",
          outputs: ["Plan de anclas y destinos", "Lista de dominios cualificados", "Calendario de publicación"],
        },
        {
          title: "Ejecución editorial",
          timing: "Mes 1-3",
          description:
            "Producción de contenido, negociación con medios y publicación con seguimiento de la indexación de cada pieza.",
          outputs: ["Enlaces publicados y verificados", "Contenido editorial propio", "Informe mensual justificado"],
        },
        {
          title: "Consolidación",
          timing: "Mes 3 en adelante",
          description:
            "Ajustamos según qué páginas responden mejor al refuerzo y reforzamos las URLs que están cerca de dar el salto competitivo.",
          outputs: ["Refuerzo de URLs prioritarias", "Revisión de anchors", "Seguimiento de autoridad del dominio"],
        },
      ],
    },
    fit: {
      title: "Cuándo el link building es la palanca correcta",
      lead: "Es un servicio potente y también el más fácil de hacer mal. Preferimos rechazar un proyecto antes que quemar un dominio.",
      yes: [
        "Tu web ya está bien resuelta técnicamente y con contenido sólido.",
        "Compites por consultas donde tus rivales tienen claramente más autoridad.",
        "Puedes sostener un proyecto continuado durante al menos 3 meses.",
        "Entiendes que la autoridad se construye con ritmo, no de golpe.",
        "Quieres saber exactamente de dónde viene cada enlace.",
      ],
      no: [
        "Tu web tiene problemas técnicos o de contenido sin resolver: los enlaces no los arreglan.",
        "Buscas volumen barato de enlaces: es la vía rápida a una penalización.",
        "Quieres enlaces desde redes privadas o granjas.",
        "Esperas un salto de posiciones en tres semanas.",
        "No aceptas que rechacemos dominios que no pasan nuestro filtro de calidad.",
      ],
    },
    faqs: {
      title: "Preguntas frecuentes sobre link building",
      lead: "Un servicio rodeado de mitos. Aquí va lo que hacemos y lo que no.",
      items: [
        {
          question: "¿Los enlaces siguen importando en 2026?",
          answer:
            "Sí, aunque su peso ha cambiado. Ya no basta con cantidad: importa la relevancia temática, el tráfico real del dominio y el contexto en el que aparece el enlace. Un enlace editorial en un medio que tu sector lee sigue siendo una de las señales de autoridad más difíciles de replicar.",
        },
        {
          question: "¿Cuántos enlaces conseguís al mes?",
          answer:
            "No trabajamos por número cerrado. Según el sector y el presupuesto, lo habitual es entre 3 y 10 enlaces mensuales de calidad verificada. Prometer una cifra fija obliga a rellenar el cupo con dominios malos, que es justo lo que provoca problemas a medio plazo.",
        },
        {
          question: "¿Cómo sé que los enlaces son seguros?",
          answer:
            "Cada enlace llega documentado: dominio, tráfico estimado, temática, página de destino y motivo de selección. Puedes revisar y vetar cualquier medio antes de publicar. Si un dominio no pasaría tu propio filtro de marca, tampoco pasa el nuestro.",
        },
        {
          question: "¿Qué hacéis con los enlaces tóxicos que ya tengo?",
          answer:
            "Los identificamos en la auditoría inicial y, si el volumen lo justifica, preparamos el archivo de desautorización. Conviene no abusar: desautorizar en exceso puede hacer más daño que bien. Solo actuamos sobre patrones claros de spam o restos de proveedores agresivos anteriores.",
        },
        {
          question: "¿Se puede contratar solo link building sin SEO?",
          answer:
            "Se puede, pero solo lo recomendamos si tu base técnica y de contenido ya está resuelta. Enlazar una web con problemas de indexación o contenido pobre es tirar presupuesto: la autoridad amplifica lo que ya hay, no sustituye lo que falta.",
        },
        {
          question: "¿Cuándo se notan los efectos?",
          answer:
            "Los enlaces necesitan ser rastreados y valorados. Lo normal es empezar a ver movimiento entre 6 y 12 semanas después de la publicación, y el efecto acumulado se aprecia con claridad a partir del tercer mes de trabajo continuado.",
        },
      ],
    },
    crossLinks: {
      title: "Servicios relacionados",
      lead: "La autoridad externa rinde cuando el resto está en orden.",
      items: [
        { label: "Servicio", title: "SEO Web Avanzado", description: "La base técnica y de contenido que los enlaces amplifican.", href: "/servicios/seo-web-ia" },
        { label: "Servicio", title: "GEO + SEO 360", description: "Autoridad en las fuentes que también leen las IA.", href: "/servicios/geo-seo-360" },
        { label: "Pilar", title: "Agencia SEO", description: "Cómo encaja el link building en nuestra metodología.", href: "/agencia-seo" },
        { label: "Contacto", title: "Diagnóstico de tu empresa", description: "Auditoría de tu perfil de enlaces en 48 horas.", href: "/agenda-consultoria" },
      ],
    },
  },

  /* ------------------------------------------------------------------ */
  "gestion-resenas-ia": {
    name: "Gestión de reseñas con IA",
    path: "/servicios/gestion-resenas-ia",
    description:
      "Reputación online y captación ética de reseñas reales con automatización IA. Propuesta personalizada según mercado, competencia, alcance y objetivos; compromiso mínimo 6 meses.",
    deliverables: {
      title: "Qué incluye la gestión de reputación",
      lead: CTA_LEAD,
      items: [
        {
          title: "Auditoría de reputación y competencia",
          frequency: "Inicio",
          description:
            "Volumen, valoración media, frecuencia y temas recurrentes en tus reseñas y en las de tus competidores directos. De ahí sale el objetivo realista, no una cifra inventada.",
        },
        {
          title: "Circuito de solicitud en el momento justo",
          frequency: "Continua",
          description:
            "Automatizaciones por WhatsApp, email o SMS que piden la reseña cuando el cliente está satisfecho, no tres semanas después. Siempre a clientes reales y siempre con opción de no participar.",
        },
        {
          title: "Respuesta a todas las valoraciones",
          frequency: "Continua",
          description:
            "Respondemos positivas y negativas en menos de una hora, con redacción personalizada y tono de marca. Responder rápido es una señal de actividad que la ficha necesita.",
        },
        {
          title: "Protocolo de crisis",
          frequency: "Bajo demanda",
          description:
            "Plan de actuación para oleadas de reseñas negativas o campañas de reputación: qué se responde, en qué orden y qué se reporta cuando incumple políticas.",
        },
        {
          title: "Análisis de sentimiento por temas",
          frequency: "Mensual",
          description:
            "Clasificamos lo que dicen tus clientes por asunto (atención, precio, plazos, calidad) para que puedas arreglar la causa, no solo la reseña. Es la parte más útil del servicio para el negocio.",
        },
        {
          title: "Informe de evolución",
          frequency: "Mensual",
          description:
            "Volumen nuevo, valoración media, tiempo de respuesta y comparativa con competidores locales. Con conclusiones accionables, no capturas de pantalla.",
        },
      ],
    },
    phases: {
      title: "Cómo se despliega",
      lead: "Seis meses porque la reputación se construye con acumulación constante de reseñas reales.",
      items: [
        {
          title: "Diagnóstico de reputación",
          timing: "Semana 1",
          description:
            "Medimos dónde estás frente a tu competencia local y detectamos los patrones que están frenando tus valoraciones.",
          outputs: ["Informe de reputación en 48 h", "Comparativa con 3 competidores", "Objetivo realista de valoración"],
        },
        {
          title: "Puesta en marcha del circuito",
          timing: "Semana 2-4",
          description:
            "Integramos los canales de solicitud con tu operativa y ponemos en marcha la respuesta a todas las reseñas pendientes.",
          outputs: ["Automatizaciones activas", "Histórico de reseñas respondido", "Plantillas de tono de marca"],
        },
        {
          title: "Acumulación y análisis",
          timing: "Mes 2-4",
          description:
            "Entra flujo constante de reseñas reales y empezamos a extraer patrones de lo que tus clientes valoran y de lo que critican.",
          outputs: ["Flujo mensual de reseñas nuevas", "Informe de sentimiento por temas", "Recomendaciones operativas"],
        },
        {
          title: "Consolidación",
          timing: "Mes 4 en adelante",
          description:
            "La valoración media se estabiliza al alza y el volumen se convierte en una barrera de entrada frente a la competencia.",
          outputs: ["Valoración media estabilizada", "Protocolo de crisis probado", "Informe mensual comparativo"],
        },
      ],
    },
    fit: {
      title: "Para quién funciona",
      lead: "Un cliente por sector y zona. La reputación es demasiado sensible para trabajar con dos competidores a la vez.",
      yes: [
        "Atiendes a un volumen regular de clientes que pueden dejar reseña.",
        "Puedes sostener un proyecto continuado durante al menos 6 meses.",
        "Tu sector se decide en gran parte por valoraciones: salud, hostelería, servicios, reformas.",
        "Estás dispuesto a corregir lo que las reseñas negativas señalen de verdad.",
        "Quieres un circuito ético y auditable, no atajos.",
      ],
      no: [
        "Quieres comprar reseñas o eliminar críticas legítimas.",
        "Tienes muy pocos clientes al mes: no hay volumen que capturar.",
        "No puedes integrar ninguna vía de contacto con el cliente tras el servicio.",
        "Buscas maquillar un problema operativo real en lugar de resolverlo.",
        "Buscas un paquete cerrado de reseñas en lugar de un sistema de reputación real.",
      ],
    },
    faqs: {
      title: "Preguntas frecuentes sobre reseñas",
      lead: "Zona sensible: aquí somos especialmente claros con lo que no hacemos.",
      items: [
        {
          question: "¿Podéis conseguirme reseñas positivas rápido?",
          answer:
            "Podemos aumentar el ritmo de reseñas reales pidiéndolas bien y en el momento adecuado. Lo que no hacemos es comprarlas ni generarlas. Además de estar prohibido por las políticas de Google, el riesgo es la eliminación de la ficha, y ese daño no se repara en meses.",
        },
        {
          question: "¿Se pueden eliminar las reseñas negativas?",
          answer:
            "Solo se pueden reportar las que incumplen las políticas: insultos, contenido falso evidente, competencia desleal o spam. Una crítica legítima no se elimina, se responde. Una buena respuesta pública convierte a muchos de los que la leen y demuestra cómo tratas los problemas.",
        },
        {
          question: "¿Cuántas reseñas necesito para competir en mi zona?",
          answer:
            "Depende de tu competencia directa, no de una cifra universal. En la auditoría medimos el volumen y la valoración media de los tres negocios que ocupan el paquete local de tu zona y fijamos un objetivo alcanzable a partir de ahí, con plazo realista.",
        },
        {
          question: "¿Las respuestas las escribe una IA?",
          answer:
            "La IA prepara un borrador con el contexto de la reseña y el tono de tu marca; una persona lo revisa antes de publicar en los casos delicados. Así conseguimos respuesta en menos de una hora sin que suene a plantilla, que es lo que más molesta a quien lee.",
        },
        {
          question: "¿Sirve para otros sitios además de Google?",
          answer:
            "Sí. Google es la prioridad porque alimenta el paquete local, pero trabajamos también las plataformas relevantes de tu sector, como portales de reservas, marketplaces o directorios verticales, según dónde estén decidiendo realmente tus clientes.",
        },
        {
          question: "¿Cómo afecta esto a mi posicionamiento local?",
          answer:
            "El volumen, la frecuencia, la valoración media y la tasa de respuesta son señales que influyen en la visibilidad del paquete local. No es el único factor, pero sí uno de los pocos que puedes mejorar de forma constante y que además convierte directamente a quien te encuentra.",
        },
      ],
    },
    crossLinks: {
      title: "Servicios relacionados",
      lead: "La reputación es una pieza del posicionamiento local, no un servicio aislado.",
      items: [
        { label: "Servicio", title: "SEO Local con IA", description: "Visibilidad en Maps con exclusividad por sector y zona.", href: "/servicios/seo-local-ia" },
        { label: "Zonas", title: "Zonas exclusivas", description: "Comprueba si tu sector sigue libre en tu zona.", href: "/zonas-exclusivas" },
        { label: "Guía", title: "Cómo funciona el SEO Local con IA", description: "El proceso completo explicado paso a paso.", href: "/como-funciona-seo-local-ia" },
        { label: "Contacto", title: "Diagnóstico de reputación", description: "Comparativa con tus competidores en 48 horas.", href: "/agenda-consultoria" },
      ],
    },
  },

  /* ------------------------------------------------------------------ */
  "creacion-web-react": {
    name: "Creación de webs en React",
    path: "/servicios/creacion-web-react",
    description:
      "Desarrollo de webs corporativas en React con foco en velocidad, SEO y conversión. Proyecto con propuesta personalizada según alcance y objetivos.",
    deliverables: {
      title: "Qué incluye el proyecto",
      lead: CTA_LEAD,
      items: [
        {
          title: "Estrategia y arquitectura de información",
          frequency: "Fase 1",
          description:
            "Antes de diseñar nada definimos qué páginas existen, qué consulta cubre cada una y cómo se enlazan. Una web bonita con mala arquitectura no posiciona ni convierte.",
        },
        {
          title: "Diseño a medida",
          frequency: "Fase 2",
          description:
            "Diseño propio alineado con tu marca, no una plantilla recoloreada. Prototipo navegable aprobado antes de escribir una línea de código.",
        },
        {
          title: "Desarrollo en React con renderizado en servidor",
          frequency: "Fase 3",
          description:
            "Stack moderno con renderizado en servidor para que el contenido sea rastreable desde el primer milisegundo. Objetivo de carga por debajo de 0,3 segundos.",
        },
        {
          title: "SEO técnico desde el primer día",
          frequency: "Fase 3",
          description:
            "Metadatos por página, datos estructurados, sitemap, canonicals y estructura de encabezados correcta. Hacer SEO después de lanzar cuesta el triple.",
        },
        {
          title: "Contenido y copy orientado a conversión",
          frequency: "Fase 3",
          description:
            "Textos redactados para responder dudas reales y llevar al contacto, con la estructura de respuesta directa que además favorece la citación en motores de IA.",
        },
        {
          title: "Migración, lanzamiento y garantía",
          frequency: "Fase 4",
          description:
            "Plan de redirecciones desde la web antigua, control de indexación post-lanzamiento y periodo de garantía con ajustes incluidos. Ninguna migración nuestra pierde el histórico.",
        },
      ],
    },
    phases: {
      title: "De la idea al lanzamiento en 8 semanas",
      lead: "Trabajamos por ciclos cerrados con entregas cada dos semanas. Sabes en todo momento qué está hecho y qué falta.",
      items: [
        {
          title: "Descubrimiento",
          timing: "Semana 1-2",
          description:
            "Objetivos de negocio, análisis de competencia, mapa de contenidos y definición de las conversiones que importan.",
          outputs: ["Mapa de arquitectura", "Análisis competitivo", "Definición de objetivos medibles"],
        },
        {
          title: "Diseño",
          timing: "Semana 2-4",
          description:
            "Sistema visual y prototipo navegable de todas las plantillas, revisado contigo antes de pasar a desarrollo.",
          outputs: ["Sistema de diseño", "Prototipo navegable", "Plantillas aprobadas"],
        },
        {
          title: "Desarrollo",
          timing: "Semana 4-7",
          description:
            "Construcción del front, integraciones, formularios, analítica, accesibilidad AA y optimización de rendimiento.",
          outputs: ["Web funcional en entorno de pruebas", "Core Web Vitals en verde", "Analítica y formularios operativos"],
        },
        {
          title: "Lanzamiento",
          timing: "Semana 7-8",
          description:
            "Migración con redirecciones, verificación de indexación, comprobación de rendimiento real y formación para tu equipo.",
          outputs: ["Redirecciones verificadas", "Web publicada y monitorizada", "Formación y documentación"],
        },
      ],
    },
    fit: {
      title: "Qué proyectos aceptamos",
      lead: "Solo abrimos unas pocas plazas de desarrollo por trimestre porque cada proyecto lo lleva el equipo senior de principio a fin.",
      yes: [
        "Tu web actual es lenta, difícil de mantener o no convierte.",
        "Quieres una web a medida como activo de negocio, no una plantilla.",
        "Quieres una base preparada para posicionar, no solo un rediseño estético.",
        "Tienes a alguien que pueda decidir y validar entregas cada dos semanas.",
        "Vienes de WordPress y quieres dejar de pelearte con plugins.",
      ],
      no: [
        "Buscas una web de plantilla en dos semanas.",
        "Necesitas un ecommerce completo: ese es el servicio de Shopify.",
        "Quieres poder editarlo todo tú sin ninguna estructura ni criterio.",
        "No tienes contenido ni intención de trabajarlo.",
        "El objetivo es solo cambiar los colores de la web actual.",
      ],
    },
    faqs: {
      title: "Preguntas frecuentes sobre desarrollo React",
      lead: "Lo que preguntan las empresas que vienen de WordPress.",
      items: [
        {
          question: "¿Por qué React y no WordPress?",
          answer:
            "Por velocidad, seguridad y control. Una web en React con renderizado en servidor carga en décimas de segundo, no depende de decenas de plugins que se rompen entre sí y no arrastra la deuda técnica típica de instalaciones con años de parches. Para SEO, esa diferencia de rendimiento se nota.",
        },
        {
          question: "¿Podré editar los contenidos yo mismo?",
          answer:
            "Sí. Conectamos un gestor de contenidos headless o un sistema de edición adaptado a lo que realmente vas a cambiar: textos, artículos, servicios e imágenes. Lo que no permitimos es romper la estructura, porque ahí es donde las webs pierden posicionamiento sin que nadie se dé cuenta.",
        },
        {
          question: "¿Perderé posiciones al migrar?",
          answer:
            "No si la migración se planifica bien. Mapeamos todas las URLs actuales, definimos redirecciones 301 una a una, mantenemos la estructura de contenidos que ya funciona y vigilamos la indexación las semanas posteriores. Las caídas de tráfico en migraciones vienen casi siempre de saltarse este trabajo.",
        },
        {
          question: "¿Qué pasa después del lanzamiento?",
          answer:
            "Hay un periodo de garantía con ajustes incluidos. A partir de ahí puedes contratar mantenimiento o el servicio de SEO Web para trabajar el posicionamiento sobre la base nueva. La web es tuya y el código también: no hay dependencia forzada con nosotros.",
        },
        {
          question: "¿Ocho semanas es un plazo real?",
          answer:
            "Sí para una web corporativa de alcance definido, siempre que las validaciones lleguen a tiempo y el contenido esté disponible. El plazo se alarga cuando hay integraciones complejas o cuando el contenido se produce sobre la marcha; eso se acota en la propuesta inicial.",
        },
        {
          question: "¿Incluye el contenido y las fotos?",
          answer:
            "Incluye la redacción de los textos orientada a conversión y SEO. La fotografía profesional y el vídeo se presupuestan aparte según lo que necesite el proyecto, o trabajamos con material que ya tengas si tiene la calidad suficiente.",
        },
      ],
    },
    crossLinks: {
      title: "Después del lanzamiento",
      lead: "Lo que suele venir a continuación en los proyectos que desarrollamos.",
      items: [
        { label: "Servicio", title: "SEO Web Avanzado", description: "Posicionar la web nueva desde una base impecable.", href: "/servicios/seo-web-ia" },
        { label: "Servicio", title: "GEO + SEO 360", description: "Visibilidad en Google y en motores de IA.", href: "/servicios/geo-seo-360" },
        { label: "Servicio", title: "Creación de ecommerce Shopify", description: "Si lo tuyo es vender producto online.", href: "/servicios/creacion-ecommerce-shopify" },
        { label: "Casos", title: "Casos de éxito", description: "Proyectos reales con métricas verificables.", href: "/casos-exito" },
      ],
    },
  },

  /* ------------------------------------------------------------------ */
  "creacion-ecommerce-shopify": {
    name: "Creación de ecommerce en Shopify",
    path: "/servicios/creacion-ecommerce-shopify",
    description:
      "Desarrollo y migración de tiendas online en Shopify con base SEO y conversión. Proyecto con propuesta personalizada según catálogo, alcance y objetivos.",
    deliverables: {
      title: "Qué incluye el proyecto de tienda",
      lead: CTA_LEAD,
      items: [
        {
          title: "Estrategia de catálogo y arquitectura",
          frequency: "Fase 1",
          description:
            "Definimos la estructura de colecciones, filtros y variantes pensando en cómo busca tu cliente, no en cómo está organizado tu almacén. Es la decisión que más condiciona el SEO de una tienda.",
        },
        {
          title: "Diseño de tienda a medida",
          frequency: "Fase 2",
          description:
            "Tema propio o personalización profunda con foco en la ficha de producto y el proceso de compra. Cada paso extra en el checkout cuesta ventas medibles.",
        },
        {
          title: "Desarrollo y rendimiento",
          frequency: "Fase 3",
          description:
            "Optimización de imágenes, control estricto de aplicaciones y velocidad en móvil. Las tiendas Shopify lentas casi siempre lo son por exceso de apps, no por la plataforma.",
        },
        {
          title: "SEO técnico y datos estructurados",
          frequency: "Fase 3",
          description:
            "Estructura de URLs, canonicals, control de filtros indexables y Schema Product con precio, disponibilidad y valoraciones desde el primer día.",
        },
        {
          title: "Integraciones y operativa",
          frequency: "Fase 3",
          description:
            "Pasarelas de pago, envíos, facturación, sincronización de stock y feed para Google Merchant preparado y validado antes de abrir.",
        },
        {
          title: "Migración y lanzamiento",
          frequency: "Fase 4",
          description:
            "Traspaso de catálogo, clientes e histórico, con redirecciones 301 producto a producto desde tu plataforma anterior. Sin perder el posicionamiento acumulado.",
        },
      ],
    },
    phases: {
      title: "El proyecto, semana a semana",
      lead: "Ciclos de dos semanas con entregas revisables. Nada se construye a ciegas durante dos meses.",
      items: [
        {
          title: "Análisis y estrategia",
          timing: "Semana 1-2",
          description:
            "Auditoría de tu catálogo actual, análisis de competidores y definición de la arquitectura comercial de la tienda.",
          outputs: ["Mapa de colecciones y filtros", "Análisis competitivo", "Plan de migración de datos"],
        },
        {
          title: "Diseño y prototipo",
          timing: "Semana 2-4",
          description:
            "Diseño de home, colección, ficha de producto y checkout, con prototipo navegable validado antes de desarrollar.",
          outputs: ["Sistema de diseño", "Prototipo de compra completo", "Plantillas aprobadas"],
        },
        {
          title: "Desarrollo e integraciones",
          timing: "Semana 4-8",
          description:
            "Construcción del tema, integraciones de pago y logística, carga de catálogo, analítica de ecommerce y feed de producto.",
          outputs: ["Tienda funcional en pruebas", "Integraciones operativas", "Feed de Merchant validado"],
        },
        {
          title: "Migración y apertura",
          timing: "Semana 8-10",
          description:
            "Redirecciones producto a producto, pruebas de compra reales, verificación de indexación y formación de tu equipo.",
          outputs: ["Redirecciones 301 verificadas", "Tienda publicada y monitorizada", "Formación y documentación"],
        },
      ],
    },
    fit: {
      title: "Qué tiendas aceptamos",
      lead: "Pocas plazas por trimestre. Una migración de ecommerce mal hecha destruye años de trabajo, así que solo tomamos las que podemos ejecutar bien.",
      yes: [
        "Ya vendes online y tu plataforma actual te está frenando.",
        "Quieres una tienda preparada para crecer, no una prueba rápida.",
        "Tu catálogo tiene estructura y datos suficientes para trabajar.",
        "Quieres una tienda pensada para posicionar, no solo para verse bien.",
        "Vienes de PrestaShop o WooCommerce y estás harto de mantenimiento.",
      ],
      no: [
        "Buscas montar una tienda de prueba con presupuesto mínimo.",
        "Todavía no has validado que tengas producto y demanda.",
        "Necesitas lógica de negocio muy específica que Shopify no soporta sin desarrollo extremo.",
        "No puedes dedicar tiempo a preparar los datos del catálogo.",
        "Esperas ventas inmediatas sin plan de captación posterior.",
      ],
    },
    faqs: {
      title: "Preguntas frecuentes sobre Shopify",
      lead: "Las dudas habituales de quien viene de PrestaShop o WooCommerce.",
      items: [
        {
          question: "¿Por qué Shopify y no WooCommerce o PrestaShop?",
          answer:
            "Por estabilidad operativa. Shopify se encarga de hosting, seguridad, pagos y actualizaciones, y tú te ocupas de vender. En PrestaShop y WooCommerce buena parte del presupuesto anual acaba yendo a mantenimiento, incidencias y compatibilidad entre módulos en lugar de a crecimiento.",
        },
        {
          question: "¿Perderé el SEO al migrar mi tienda?",
          answer:
            "No si se mapea el catálogo completo. Preparamos redirecciones 301 producto a producto y categoría a categoría, conservamos la estructura de contenidos que ya rinde y monitorizamos la indexación las semanas siguientes. Las caídas graves en migraciones vienen de redirigirlo todo a la home.",
        },
        {
          question: "¿Shopify es bueno para SEO?",
          answer:
            "Es sólido si se trabaja bien. Tiene limitaciones conocidas, como la estructura fija de URLs de producto, pero a cambio ofrece rendimiento estable y facilidad para implementar datos estructurados. En la práctica, el factor que más pesa es cómo se organiza el catálogo, no la plataforma.",
        },
        {
          question: "¿Cuántas aplicaciones vais a instalar?",
          answer:
            "Las mínimas imprescindibles. Cada app añade código que se carga en cada visita y las tiendas con veinte apps activas suelen ser lentas por eso. Preferimos resolver en el tema lo que se pueda resolver en el tema y justificar cada aplicación que se queda.",
        },
        {
          question: "¿Incluye la carga de productos?",
          answer:
            "Incluye la migración del catálogo existente con sus datos, imágenes y variantes. Si necesitas reescribir descripciones o generar contenido nuevo para las fichas, eso forma parte del servicio de SEO Ecommerce, que suele contratarse después del lanzamiento.",
        },
        {
          question: "¿Qué plazo tiene el proyecto?",
          answer:
            "Entre 8 y 10 semanas para una tienda de alcance definido, según el tamaño del catálogo y la complejidad de las integraciones. Las variables que más alargan el plazo son la calidad de los datos de origen y los tiempos de validación por tu parte; ambas se acotan en la propuesta.",
        },
      ],
    },
    crossLinks: {
      title: "Después de abrir la tienda",
      lead: "El lanzamiento es el principio, no el objetivo.",
      items: [
        { label: "Servicio", title: "SEO Ecommerce con IA", description: "Posicionar categorías y fichas desde el primer mes.", href: "/servicios/seo-ecommerce-ia" },
        { label: "Servicio", title: "Google Merchant con IA", description: "Feed optimizado para Shopping desde la apertura.", href: "/servicios/google-merchant-ia" },
        { label: "Servicio", title: "Publicidad SEM con IA", description: "Tráfico inmediato mientras el orgánico arranca.", href: "/servicios/publicidad-sem-ia" },
        { label: "Casos", title: "Casos de éxito", description: "Proyectos de ecommerce con resultados reales.", href: "/casos-exito" },
      ],
    },
  },
};

export function getServicioContenido(slug: string): ServiceContent | undefined {
  return serviciosContenido[slug];
}
